<template>
  <ion-header :class="isLarge ? 'ion-no-border header-navbar' : 'ion-no-border'" collapse="fade">
    <ion-toolbar class="padding-xs header-toolbar-small" color="primary">
      <template v-if="isHome">
        <div class="header-logo">
          <ion-img v-if="arriboLogo" :src="arriboLogo" alt="Arribo Logo"></ion-img>
        </div>
      </template>
      <template v-else>
        <ion-label
          class="ion-padding-start ion-padding-end text-weight-bold text-lg"
          v-if="!showBackButton"
          >{{ title }}</ion-label
        >
        <ion-buttons v-else @click="$emit('back-button')" slot="start">
          <ion-button
            color="primary"
            shape="round"
            size="small"
            fill="clear"
            class="ion-no-padding"
          >
            <ion-icon color="white" size="large" :icon="arrowBack"></ion-icon>
          </ion-button>
          <ion-label class="text-weight-bold">{{ title }}</ion-label>
        </ion-buttons>
      </template>
      <template v-if="showActions && user">
        <template v-if="!showEdit">
          <ion-buttons slot="end">
            <ion-menu-button :auto-hide="false" @click="router.push({ name: 'notifications' })">
              <ion-icon :icon="notifications" size="small" />
              <ion-badge
                v-if="notificationsInPage.filter((not) => !not.isRead).length > 0"
                color="error"
                >{{ notificationsInPage.filter((not) => !not.isRead).length }}</ion-badge
              >
            </ion-menu-button>
            <ion-menu-button :auto-hide="false" @click="router.push({ name: 'profile' })">
              <UserAvatar />
            </ion-menu-button>
          </ion-buttons>
        </template>
        <template v-else>
          <ion-buttons slot="end">
            <ion-menu-button :auto-hide="false" @click="toggleEditProfile = true">
              <ion-icon :icon="pencil" size="small" />
            </ion-menu-button>
          </ion-buttons>
        </template>
      </template>
      <template v-if="showShareButton">
        <ion-buttons slot="end">
          <ion-menu-button :auto-hide="false" @click="share">
            <ion-icon :icon="shareOutline" />
          </ion-menu-button>
        </ion-buttons>
      </template>
      <template v-if="showLanguage">
        <ion-buttons slot="end">
          <ion-select
            mode="ios"
            aria-label="language"
            v-model="languageSelected"
            :value="languageSelected"
            placeholder="Language"
            color="primary"
            @ion-change="changeLanguage"
          >
            <ion-select-option
              :value="language.id"
              v-for="language in languageOptions"
              :key="language.id"
              >{{ language.label }}</ion-select-option
            >
          </ion-select>
        </ion-buttons>
      </template>

      <slot name="end" />
    </ion-toolbar>
  </ion-header>
</template>

<script setup lang="ts">
  import {
    IonHeader,
    IonToolbar,
    IonMenuButton,
    IonButtons,
    IonButton,
    IonIcon,
    IonImg,
    IonLabel,
    IonBadge,
    IonSelect,
    IonSelectOption,
  } from '@ionic/vue';
  import UserAvatar from '@/components/shared/avatar/UserAvatar.vue';
  import { arrowBack, notifications, pencil, shareOutline } from 'ionicons/icons';
  import arriboLogo from '@/assets/images/arribo-logo.svg';
  import { useProfile } from '@/composables/useProfile';
  import router from '@/router';
  import { useNotificationsStore } from '@/stores/notifications-store';
  import { storeToRefs } from 'pinia';
  import { useAuthStore } from '@/stores/auth-store';
  import { useLanguages } from '@/utils/constants/general-constants';
  import { onMounted } from 'vue';
  import { i18n } from '@/plugins/i18n';
  import { Share } from '@capacitor/share';
  import { BASE_URL } from '@/utils/constants/api-constants';

  const { notificationsInPage } = storeToRefs(useNotificationsStore());

  const { toggleEditProfile, languageSelected } = useProfile();
  const { user } = storeToRefs(useAuthStore());
  const languageOptions = useLanguages();

  defineProps({
    isHome: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLarge: {
      type: Boolean,
      required: false,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: true,
      default: '',
    },
    showEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLanguage: {
      type: Boolean,
      required: false,
      default: false,
    },
    showActions: {
      type: Boolean,
      required: false,
      default: true,
    },
    showShareButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const share = async () => {
    const path = window.location.href.split('localhost/')[1];
    const url = `${BASE_URL.WEB_URL}${path}`;
    await Share.share({ url });
  };

  const changeLanguage = (event: any) => {
    languageSelected.value = event.detail.value;
    i18n.global.locale.value = languageSelected.value;
  };

  onMounted(() => {
    languageSelected.value = i18n.global.locale.value;
  });
</script>

<style lang="scss" scoped>
  /* .header-toolbar {
    height: 130px;
    padding-top: 10px;
  } */

  .header-toolbar-small {
    height: 70px;
    padding-top: 10px;
  }

  .header-logo {
    position: relative;
    margin-top: 10px;
    width: 100%;
    display: flex;

    ion-img {
      width: 100px;
    }
  }

  ion-select {
    --placeholder-color: white;
    --border-color: white;
    --ripple-color: white;
  }

  ion-select::part(icon) {
    color: white;
    opacity: 1;
  }
</style>
